import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useRouterStore } from '@/stores/router'
import { useChatStore } from '@/stores/chat'
import { checkAndRedirectLogin } from '@/api/request'

// 静态路由配置（无需权限的基础路由）
const constantRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/Login.vue'),
    meta: { title: '登录', ignoreAuth: true }
  },
  {
    path: '/force-change-password',
    name: 'ForceChangePassword',
    component: () => import('@/views/login/ForceChangePassword.vue'),
    meta: { title: '修改密码' }
  },
  {
    path: '/cashier/:cashierNo',
    name: 'CashierPublic',
    component: () => import('@/views/cashier/CashierPublic.vue'),
    meta: { title: '收银台', ignoreAuth: true }
  },
  {
    path: '/',
    component: () => import('@/components/Layout/MainLayout.vue'),
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/Dashboard.vue'),
        meta: { title: '首页工作台' }
      },
      {
        path: 'workflow/list',
        name: 'WorkflowList',
        component: () => import('@/views/system/workflow/WorkflowList.vue'),
        meta: { title: '流程列表' }
      },
      {
        path: 'workflow/designer',
        name: 'WorkflowDesigner',
        component: () => import('@/views/system/workflow/WorkflowDesigner.vue'),
        meta: { title: '流程设计器' }
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/profile/Profile.vue'),
        meta: { title: '个人中心' }
      },
      {
        path: 'messages',
        name: 'MessageCenter',
        component: () => import('@/views/messages/MessageCenter.vue'),
        meta: { title: '消息中心' }
      },
      {
        path: 'schedule',
        name: 'Schedule',
        component: () => import('@/views/schedule/ScheduleManagement.vue'),
        meta: { title: '日程管理' }
      },
      {
        path: 'todo',
        name: 'Todo',
        component: () => import('@/views/todo/TodoManagement.vue'),
        meta: { title: '待办事项' }
      },
      {
        path: 'reminders/definitions',
        name: 'ReminderDefinitionList',
        component: () => import('@/views/reminder/ReminderDefinitionList.vue'),
        meta: { title: '提醒定义' }
      },
      {
        path: 'reminders/my-tasks',
        name: 'MyReminderTaskList',
        component: () => import('@/views/reminder/MyReminderTaskList.vue'),
        meta: { title: '我的提醒' }
      },
      {
        path: 'referrer',
        name: 'Referrer',
        component: () => import('@/views/referrer/ReferrerManagement.vue'),
        meta: { title: '推荐人管理' }
      },
      {
        path: 'chat',
        name: 'Chat',
        component: () => import('@/views/chat/ChatView.vue'),
        meta: { title: '聊天' }
      },
      {
        path: 'banner',
        name: 'BannerManagement',
        component: () => import('@/views/banner/BannerManagement.vue'),
        meta: { title: 'Banner 管理' }
      },
      {
        path: 'finance/withdrawal-review',
        name: 'WithdrawalReview',
        component: () => import('@/views/finance/WithdrawalReview.vue'),
        meta: { title: '提现审核' }
      },
      {
        path: 'commission/withdrawal',
        name: 'CommissionWithdrawal',
        component: () => import('@/views/commission/WithdrawalManagement.vue'),
        meta: { title: '提现管理' }
      },
      {
        path: 'esign/templates',
        name: 'EsignTemplateManagement',
        component: () => import('@/views/esign/EsignTemplateManagement.vue'),
        meta: { title: '电子签模板' }
      },
      {
        path: 'esign/flows',
        name: 'EsignFlowManagement',
        component: () => import('@/views/esign/EsignFlowManagement.vue'),
        meta: { title: '签署流程' }
      },
      // {
      //   path: 'esign/tencent',
      //   name: 'TencentEsignResourceManagement',
      //   component: () => import('@/views/esign/TencentEsignResourceManagement.vue'),
      //   meta: { title: '腾讯资源' }
      // },
      {
        path: 'esign/callback-logs',
        name: 'EsignCallbackLogManagement',
        component: () => import('@/views/esign/EsignCallbackLogManagement.vue'),
        meta: { title: '回调日志' }
      },
      {
        path: 'assets/repository',
        name: 'AssetRepository',
        component: () => import('@/views/assets/AssetRepository.vue'),
        meta: { title: '素材仓库' }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'PageNotFound',
    component: () => import('@/views/PageNotFound.vue'),
    meta: { title: '页面不存在' }
  }
]

// 创建路由实例
const createRouterInstance = () => {
  return createRouter({
    history: createWebHistory(),
    routes: constantRoutes,
    scrollBehavior: () => ({ top: 0 })
  })
}

const router = createRouterInstance()

// 重置路由（用于动态路由切换时）
export function resetRouter() {
  const newRouter = createRouterInstance()
  router.matcher = newRouter.matcher
}

// 路由前置守卫
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  const routerStore = useRouterStore()
  const isLoggedIn = userStore.isLoggedIn
  const isLoginPage = to.path === '/login'

  // 设置页面标题
  document.title = `${to.meta.title || '小到大企服平台'} - 小到大企服平台`

  // 是否忽略鉴权
  const ignoreAuth = to.meta.ignoreAuth

  // 未登录且需要鉴权，跳转到登录页
  if (!isLoggedIn && !ignoreAuth) {
    // 检查是否有 401 重定向标记
    if (checkAndRedirectLogin()) {
      next({ path: '/login', query: { redirect: to.fullPath } })
      return
    }
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
    return
  }

  // 已登录时清除重定向标记，防止刷新后仍然强制跳登录
  if (isLoggedIn) {
    checkAndRedirectLogin()
  }

  // 已登录且访问登录页，直接跳转到首页
  if (isLoggedIn && isLoginPage) {
    next({ path: '/dashboard' })
    return
  }

  // 强制修改密码：只允许访问修改密码页，跳过动态路由加载
  if (isLoggedIn && userStore.forceChangePassword) {
    if (to.path !== '/force-change-password') {
      next({ path: '/force-change-password' })
    } else {
      next()
    }
    return
  }

  // 已登录但用户信息未加载，先尝试加载用户信息
  if (isLoggedIn && !userStore.isUserInfoLoaded) {
    try {
      await userStore.fetchUserInfo()
    } catch (error) {
      console.error('Failed to load user info', error)
    }
  }

  // 已登录且动态路由未加载，尝试加载
  if (isLoggedIn && !routerStore.isRoutesLoaded) {
    try {
      const success = await routerStore.loadDynamicRoutes()
      if (success) {
        // 动态路由加载成功后，重新导航到目标页
        if (to.name === 'PageNotFound') {
          next({ path: to.fullPath, replace: true })
        } else {
          next({ ...to, replace: true })
        }
      } else {
        next()
      }
    } catch (error) {
      console.error('加载动态路由失败', error)
      next()
    }
    return
  }

  // 已登录，确保 WebSocket 已初始化
  if (isLoggedIn && !isLoginPage) {
    const chatStore = useChatStore()
    chatStore.initWebSocket()
  }

  // 动态路由已加载或不需要加载，正常放行
  next()
})

// 路由后置守卫
router.afterEach(() => {
  // 可在此处理切换路由后的通用逻辑
})

export default router
